<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterEntryInvoiceSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    editor: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onOptionSelected = (...args) => {
    const [{ option, value }] = args;
    if (_.isObject(option)) {
      props.editor.invoice = _.cloneDeep(option);
    }
  };

  const messages = ref({
    searchPlaceholder: $fn.value.pgettext('Register', 'Search for invoice'),
  });
  const ux = ref([]);
  const b_2248226175642056_dataSource = computed(() => ({
    name: 'suggest_invoice',
    parameters: {
      company_id: props.editor.version.company_id,
      partner_id: props.editor.partner ? props.editor.partner.id : null,
    },
  }));

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
    b_2248226175642056_dataSource: b_2248226175642056_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_2248226175642056_dataSource', nodeId: '2248226175642056' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux, b_2248226175642056_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editor,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editor');
    }
  );

  const act_2248226175642056_script_jyubmss0z_value_callable = async (
    $event
  ) => {
    return onOptionSelected($event);
  };
  const act_2248226175642056_script_jyubmss0z_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_jyubmss0z_value_callable($event);
      },
    };
  };
  const act_5534025912102772_emit_inlo40011_getActionArgs = async ($event) => {
    return {
      event: 'close',
    };
  };
  const act_5534025912102772_showModal_fnnvrzxxs_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        registerEntry: props.editor.id,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_tb9njjk1j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_z2k88ne9c =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_tb9njjk1j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_z2k88ne9c],
    event: 'reload',
  });
  const act_2248226175642056_script_jyubmss0z = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_jyubmss0z_getActionArgs,
  });
  const inst_evh_2248226175642056_optionSelected_86ynkj6g5 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_jyubmss0z,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_optionSelected_86ynkj6g5],
      event: 'optionSelected',
    });
  const act_5534025912102772_emit_inlo40011 = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_inlo40011_getActionArgs,
  });
  const inst_evh_5534025912102772_clickPreventStop_13xkb36yv =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_inlo40011,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const act_5534025912102772_showModal_fnnvrzxxs =
    new actionTypes.ShowModalAction({
      actionArgs: act_5534025912102772_showModal_fnnvrzxxs_getActionArgs,
    });
  const inst_evh_5534025912102772_clickPreventStop_ofd7mrlte =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_showModal_fnnvrzxxs,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5534025912102772_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5534025912102772_clickPreventStop_13xkb36yv,
        inst_evh_5534025912102772_clickPreventStop_ofd7mrlte,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_z2k88ne9c,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_optionSelected_86ynkj6g5,
    inst_evh_2248226175642056_optionSelected,
    inst_evh_5534025912102772_clickPreventStop_13xkb36yv,
    inst_evh_5534025912102772_clickPreventStop_ofd7mrlte,
    inst_evh_5534025912102772_clickPreventStop,
  };

  const evh_7315092382398562_reload_z2k88ne9c = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_z2k88ne9c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_optionSelected_86ynkj6g5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_optionSelected_86ynkj6g5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_optionSelected = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_clickPreventStop_13xkb36yv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_clickPreventStop_13xkb36yv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_clickPreventStop_ofd7mrlte = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_clickPreventStop_ofd7mrlte.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpSelect2
      name="invoice"
      v-model="editor.invoice"
      :value="editor.invoice"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (editor.invoice =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
      bind-object
      :placeholder="messages.searchPlaceholder"
      :dataSource="b_2248226175642056_dataSource"
      valueField="id"
      textField="invoice_number"
      clearable
      optionTemplate="<UcSuggestInvoiceOption :option=option></UcSuggestInvoiceOption>"
      selectedOptionTemplate='<UcSuggestInvoiceOption v-if="option.id" :option=option></UcSuggestInvoiceOption>'
      :readonly="editor.isReadonly"
      @optionSelected="evh_2248226175642056_optionSelected($event, {})"
    >
    </ItpSelect2>
    <ItpBox direction="column" v-if="loaded">
      <ItpBox
        v-if="
          editor.invoice &&
          !editor.invoice.id &&
          editor.$_resource.invoice &&
          editor.$_resource.invoice.invoice_number
        "
      >
        <ItpButton
          :text="pgettext('RegisterEntry', 'Create invoice...')"
          size="sm"
          variant="light"
          :tooltip="pgettext('RegisterEntry', 'Record a new invoice.')"
          :icon="$config.addIcon"
          v-if="editor.id"
          @click.prevent.stop="
            evh_5534025912102772_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
